import { useState } from "react";

import {
  FINANCEIRO_OBRAS_TABLE_COLUMNS_KEY,
  FINANCEIRO_OBRAS_TABLE_DENSITY_KEY,
} from "@/core/constants/keys";
import { useTableSettings, useToast } from "@/ds";
import { useMediaQuery } from "@/hooks/use-media-query";
import { useTableSettingsAppliedToast } from "@/shared/hooks";
import { usePermissoes } from "@/modules/permissoes";
import { createExportFeedback, describeReportFilters, useFileExport } from "@/modules/export";
import { exportFinanceiroObras } from "@/modules/financeiro-obras";
import type { FinanceiroObraItem } from "@/modules/financeiro-obras";
import {
  FINANCEIRO_ALL_COLUMNS,
  FINANCEIRO_EXPORT_FILE_NAME,
  FINANCEIRO_MAIN_COLUMNS,
  MOBILE_QUERY,
  STATUS_OPTIONS,
} from "./financeiro-obras.constants";
import {
  formatPeriodo,
  migrateFinanceiroTableSettings,
  toSavedValue,
} from "./financeiro-obras.utils";
import { useFinanceiroFiltrosColunas } from "./hooks/use-financeiro-filtros-colunas";
import { useFinanceiroObrasCriterios } from "./hooks/use-financeiro-obras-criterios";
import type {
  FinanceiroObrasColumnKey,
  FinanceiroObrasScreenViewModel,
} from "./types-financeiro-obras";

export function useFinanceiroObrasScreen(): FinanceiroObrasScreenViewModel {
  // Dispara a consulta das permissões já ao abrir a tela: o detalhe da obra mostra a ação das fichas sem esperar a rede.
  usePermissoes();
  const isMobile = useMediaQuery(MOBILE_QUERY);
  const toast = useToast();

  const { allFilterColumns, filterColumns, tipoOptions, isTipoDisabled } =
    useFinanceiroFiltrosColunas(isMobile);
  const criterios = useFinanceiroObrasCriterios(isMobile, filterColumns);
  const { view, search, filters, appliedSort } = criterios;

  const [detalheObra, setDetalheObra] = useState<FinanceiroObraItem | null>(null);
  // A migração é idempotente e roda antes do hook, no primeiro render.
  useState(migrateFinanceiroTableSettings);
  const settings = useTableSettings<FinanceiroObrasColumnKey>({
    densityKey: FINANCEIRO_OBRAS_TABLE_DENSITY_KEY,
    columnsKey: FINANCEIRO_OBRAS_TABLE_COLUMNS_KEY,
    allColumns: FINANCEIRO_ALL_COLUMNS,
    mainColumns: FINANCEIRO_MAIN_COLUMNS,
  });
  const [listResult, setListResult] = useState({ status: "loading", total: 0 });

  /**
   * Exportar Visualização (Padrões Globais): o servidor gera o arquivo com busca, filtros e
   * ordenação em vigor, sem paginação, e só as colunas visíveis da Lista na ordem da tela (a lupa
   * fica fora). Na visão Cards o arquivo sai na visão de tabela.
   */
  const exportView = useFileExport({
    fileName: FINANCEIRO_EXPORT_FILE_NAME,
    loadFile: (format) =>
      exportFinanceiroObras({
        busca: search,
        filters,
        filtros: describeReportFilters(search, allFilterColumns, toSavedValue(filters), {
          periodo: formatPeriodo(filters),
        }),
        sort: appliedSort,
        colunas: settings.visibleColumns,
        format,
      }),
    isEmpty: () => view === "lista" && listResult.status === "success" && listResult.total === 0,
    ...createExportFeedback(toast),
  });

  const notifySettingsApplied = useTableSettingsAppliedToast(toast);

  return {
    isMobile,
    ...criterios,
    settings,
    changeListResult: setListResult,
    statusOptions: STATUS_OPTIONS,
    tipoOptions,
    isTipoDisabled,
    filterColumns,
    exportView,
    notifySettingsApplied,
    detalheObra,
    abrirDetalhe: (obra) => setDetalheObra(obra),
    fecharDetalhe: () => setDetalheObra(null),
  };
}
